/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * `sizes` hints for the tile images, in breakpoint order: base, sm, md, lg, xl, 2xl.
 *
 * Their own module rather than constants inside `grid.tsx`, because one of them has to be exported
 * for the search route to pass, and a module that exports both a component and a plain value loses
 * Fast Refresh granularity (`react-refresh/only-export-components`, linted at `--max-warnings 0`).
 * Keeping all three together is the point — they are derived against each other, and a set that
 * lived apart from its siblings would be re-derived in isolation and drift.
 *
 * Derived from the real layout rather than estimated — specifically the **search** page's:
 *   container  `.section-container` → `max-w-screen-2xl` (1536px), `px-4 sm:px-8 lg:px-16`
 *   sidebar    `w-64` (256px) + the row's `gap-2` (8px) = **264px** taken at `lg`+
 *   grid gap   `gap-x-4` (16px) between columns
 *
 * NOTE the previous comment here claimed the sidebar cost 288px ("w-64 + gap-8"). The row is
 * `gap-2`, so it is 264px. The old values were also derived for a 2/3/4 column grid and were
 * wrong at every step once the columns changed.
 *
 * The two consumers no longer share a container, so ONE set of hints cannot be exact for both.
 * The category PLP now uses `max-w-[90rem]` with `p-4`/`lg:p-8` and a `minmax(0,1fr)
 * minmax(0,3fr)` grid, which renders slightly WIDER columns than these hints describe —
 * measured at 1024px: search 288px vs category 309px per column. `sizes` too small means the
 * browser may pick a smaller candidate than ideal, and too large means over-fetching; a ~7%
 * difference sits inside the 1x/2x candidate spacing, so neither route changes which image it
 * downloads. Left as one shared set rather than forked, for the same drift reason as the column
 * rule above. Re-derive if the containers diverge further (CRCB2B-87).
 *
 * **They have now diverged further (CRCB2B-348).** Search's panel went from `lg:w-64` to
 * `lg:w-84`, so it takes 368px rather than the 264px above and its columns fall well outside the
 * ~7% tolerance this set was left shared on. That route therefore passes its own
 * `searchPanelImageWidths` below, and THIS array now describes the category PLP alone; its
 * values are unchanged, and re-deriving them against that container is its own measured change.
 *
 * The two arrays still differ even though the column count no longer does: three columns in a
 * panel-narrowed content area are three NARROWER columns, and a `sizes` value that ignores that
 * silently over-fetches. Over-fetching does not fail, which is why it needs deriving rather
 * than eyeballing.
 */
export const responsiveImageWidthsWithRefinements = [
    '92vw', // base: 1 col — 100vw − 32px padding
    '45vw', // sm:   2 cols — (100vw − 64px padding − 16px gap) / 2
    '46vw', // md:   2 cols — padding is still sm:px-8 until lg
    '22vw', // lg:   3 cols — (100vw − 128px padding − 264px sidebar − 32px gaps) / 3
    '24vw', // xl:   3 cols, same formula, wider viewport
    '380px', // 2xl: container is CAPPED at 1536px, so vw would keep growing while the tile does not
];

/** Same layout minus the 264px sidebar, so every step from `lg` is wider. */
export const responsiveImageWidthsWithoutRefinements = [
    '92vw', // base: 1 col — identical, the panel is not rendered at this width
    '45vw', // sm:   2 cols — identical
    '46vw', // md:   2 cols — identical
    '29vw', // lg:   3 cols — (100vw − 128px padding − 32px gaps) / 3
    '30vw', // xl:   3 cols, same formula
    '460px', // 2xl: capped container again
];

/**
 * The **search** route's panel-open hints. Passed as `ProductGrid`'s `panelWidths`; the closed case
 * reuses `responsiveImageWidthsWithoutRefinements`, which is container-agnostic from `lg` up.
 *
 * Search never adopted the category PLP's page grid (CRCB2B-87 deliberately left its layout
 * alone), so its panel is an explicit `lg:w-84` (336px) inside a `flex … gap-8` row — **368px**
 * taken at `lg`+, not 264px. Sharing one array across a 104px difference is what silently
 * over-fetches: the shared `22vw` asks for 225px at 1024px where the real column is 165px.
 *
 *   container  `.section-container` → `max-w-screen-2xl` (1536px), `px-4 sm:px-8 lg:px-16`
 *   panel      `lg:w-84` (336px) + the row's `gap-8` (32px) = 368px at `lg`+
 *   grid gap   `gap-x-4` (16px) between columns
 *
 * Fold this back into the shared set if search ever moves onto the page grid.
 */
export const searchPanelImageWidths = [
    '92vw', // base: 1 col — no panel at this width, identical to the shared set
    '45vw', // sm:   2 cols — identical
    '46vw', // md:   2 cols — identical
    '16vw', // lg:   3 cols — (100vw − 128px padding − 368px panel+gap − 32px gaps) / 3
    '20vw', // xl:   3 cols, same formula, wider viewport
    '336px', // 2xl: container is CAPPED at 1536px — (1536 − 128 − 368 − 32) / 3
];
